import type { VerifyResponse } from '@dynamic-labs/sdk-api-core';

import { getCore } from '../../../../../client/core/getCore';
import { getDefaultClient } from '../../../../../client/defaultClient';
import { InvalidRedirectStorageStateError } from '../../../../../errors/InvalidRedirectStorageStateError';
import { MissingRedirectStorageStateError } from '../../../../../errors/MissingRedirectStorageStateError';
import { MissingSocialUrlParamError } from '../../../../../errors/MissingSocialUrlParamError';
import { createApiClient } from '../../../../apiClient';
import { consumeCaptchaToken } from '../../../../captcha/consumeCaptchaToken';
import type { User } from '../../../../user/user.types';
import { updateAuthFromVerifyResponse } from '../../../updateAuthFromVerifyResponse';
import { redirectStateStorageKeySchema } from '../redirectStateStorageSchema';

type CompleteSocialAuthenticationParams = {
  url: URL;
};

/**
 * Completes the social authentication flow after OAuth redirect.
 *
 * This function processes the OAuth callback URL with authorization codes
 * and completes the user authentication with the social provider.
 *
 * @param params.url - The callback URL containing OAuth response parameters.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to the authenticated user or null.
 * @throws MissingSocialUrlParamError If required OAuth parameters are missing.
 * @throws InvalidRedirectStorageStateError If the stored state doesn't match.
 */
export const completeSocialAuthentication = async (
  { url }: CompleteSocialAuthenticationParams,
  client = getDefaultClient()
): Promise<User | null> => {
  const core = getCore(client);
  const apiClient = createApiClient({}, client);

  await core.initTrack.waitForAll();

  const dynamicOauthState = url.searchParams.get('dynamicOauthState');
  const dynamicOauthCode = url.searchParams.get('dynamicOauthCode');

  if (!dynamicOauthState) {
    throw new MissingSocialUrlParamError('dynamicOauthState');
  }

  if (!dynamicOauthCode) {
    throw new MissingSocialUrlParamError('dynamicOauthCode');
  }

  const storedSocialRedirectState = await core.storage.getItem(
    redirectStateStorageKeySchema
  );

  if (!storedSocialRedirectState) {
    throw new MissingRedirectStorageStateError();
  }

  if (storedSocialRedirectState.state !== dynamicOauthState) {
    throw new InvalidRedirectStorageStateError();
  }

  const { provider, codeVerifier } = storedSocialRedirectState;

  await core.storage.removeItem(redirectStateStorageKeySchema);

  let response: VerifyResponse;

  // if user is alreadyauthenticated, we're verifying the social auth to link it to the user
  if (client.user) {
    response = await apiClient.oauthVerify({
      environmentId: core.environmentId,
      oauthRequest: {
        code: dynamicOauthCode,
        codeVerifier,
        state: dynamicOauthState,
      },
      providerType: provider,
    });
  } else {
    // if user is not authenticated, we're signing in with the social auth
    response = await apiClient.oauthSignIn({
      environmentId: core.environmentId,
      oauthRequest: {
        captchaToken: consumeCaptchaToken(client),
        code: dynamicOauthCode,
        codeVerifier,
        state: dynamicOauthState,
      },
      providerType: provider,
    });
  }

  updateAuthFromVerifyResponse({ response }, client);

  return response.user;
};
